import { HttpErrorResponse, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';
import { NewRelicAgentService } from '../new-relic-agent/new-relic-agent.service';

/**
 * HTTP Error Interceptor for New Relic Integration
 *
 * @description
 * Intercepts all HTTP requests and responses to:
 * 1. Track HTTP errors with rich context
 * 2. Monitor API performance and success rates
 * 3. Provide detailed error analytics for financial operations
 *
 * @businessFlow
 * Request → Measure timing → Success: track performance | Error: track detailed context
 *
 * **Critical for financial apps**: All API failures are tracked with user context
 * **Performance monitoring**: Track slow APIs that affect user experience
 * **Error analytics**: Rich context for debugging production issues
 */
export const httpErrorInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next) => {
    const newRelicAgent = inject(NewRelicAgentService);

    // Record request start time for performance tracking
    const startTime = performance.now();
    const method = req.method;
    const url = req.url;

    return next(req).pipe(
        tap((event) => {
            // Track successful responses
            if (event instanceof HttpResponse) {
                const duration = performance.now() - startTime;

                // Only track API calls (not assets)
                if (url.includes('/api/') || url.includes('paper') || url.includes('mars')) {
                    newRelicAgent.trackHttpSuccess(url, method, event.status, duration);

                    // Track specific business events based on endpoints
                    trackBusinessEvents(newRelicAgent, url, method, event.status, duration);
                }
            }
        }),
        catchError((error: HttpErrorResponse) => {
            const duration = performance.now() - startTime;

            // Extract request data for context (raw data - no sanitization)
            const requestData = extractRequestData(req);

            // Report HTTP error to New Relic with full context
            newRelicAgent.handleHttpError(error, duration, requestData);

            // Track specific error patterns for financial operations
            trackFinancialErrorPatterns(newRelicAgent, error, url, method);

            // Re-throw the error to maintain normal error handling flow
            return throwError(() => error);
        }),
    );
};

/**
 * Track business-specific events based on API endpoints
 */
function trackBusinessEvents(
    newRelicAgent: NewRelicAgentService,
    url: string,
    method: string,
    status: number,
    duration: number,
): void {
    if (url.includes('request-otp')) {
        newRelicAgent.trackBusinessEvent('otp_request_success', {
            endpoint: 'request-otp',
            method,
            duration,
            status,
        });
    } else if (url.includes('validate-otp')) {
        newRelicAgent.trackBusinessEvent('otp_validate_success', {
            endpoint: 'validate-otp',
            method,
            duration,
            status,
        });
    } else if (url.includes('pin-transactions')) {
        newRelicAgent.trackBusinessEvent('pin_transaction_success', {
            endpoint: 'pin-transactions',
            method,
            duration,
            status,
        });
    }
}

/**
 * Track financial-specific error patterns for analysis
 */
function trackFinancialErrorPatterns(
    newRelicAgent: NewRelicAgentService,
    error: HttpErrorResponse,
    url: string,
    method: string,
): void {
    const errorCode = error.error?.error_code || 'UNKNOWN';
    const status = error.status;

    // Track OTP-related errors
    if (url.includes('otp')) {
        newRelicAgent.trackBusinessEvent('otp_error', {
            errorCode,
            status,
            endpoint: url.includes('request-otp') ? 'request-otp' : 'validate-otp',
            method,
        });
    }

    // Track PIN transaction errors
    if (url.includes('pin-transactions')) {
        newRelicAgent.trackBusinessEvent('pin_transaction_error', {
            errorCode,
            status,
            endpoint: 'pin-transactions',
            method,
        });
    }

    // Track rate limiting and blocking patterns
    if (status === 429) {
        newRelicAgent.trackBusinessEvent('rate_limit_error', {
            endpoint: url,
            method,
        });
    } else if (status === 423) {
        newRelicAgent.trackBusinessEvent('account_locked_error', {
            endpoint: url,
            method,
        });
    }

    // Track server errors that might indicate infrastructure issues
    if (status >= 500) {
        newRelicAgent.trackBusinessEvent('server_error', {
            status,
            endpoint: url,
            method,
            errorCode,
        });
    }
}

/**
 * Extract raw request data for error context (no sanitization)
 */
function extractRequestData(req: HttpRequest<unknown>): any {
    try {
        const requestData: any = {
            method: req.method,
            url: req.url,
            headers: extractHeaders(req.headers),
        };

        // Include body for POST/PUT requests (raw data)
        if (req.body && (req.method === 'POST' || req.method === 'PUT')) {
            requestData.body = req.body;
        }

        return requestData;
    } catch (error) {
        return { method: req.method, url: req.url, error: 'Failed to extract request data' };
    }
}

/**
 * Extract all headers (no filtering)
 */
function extractHeaders(headers: any): Record<string, string> {
    const allHeaders: Record<string, string> = {};

    try {
        headers.keys().forEach((key: string) => {
            allHeaders[key] = headers.get(key);
        });
    } catch {
        // Fallback if headers iteration fails
    }

    return allHeaders;
}
